import React, { useEffect, useRef, useState } from 'react';
import { formatColor } from '../utils/colorFormats';
import { ANIMATION_TIMINGS } from '../constants';
import './ColorSwatches.css';

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="copy-icon">
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
    </svg>
  );
}

function getLuminance(hex) {
  // Remove # if present
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  // Convert to relative luminance
  const [rs, gs, bs] = [r, g, b].map(val => 
    val <= 0.04045 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function isLightColor(hex) {
  return getLuminance(hex) > 0.5;
}

export default function ColorSwatches({ colors, onCopy, isLoading = false, colorFormat = 'hex', isPageLoaded = false }) {
  const hasColors = Array.isArray(colors) && colors.length > 0;
  const swatchListRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [displayFormat, setDisplayFormat] = useState(colorFormat);
  const [displayColors, setDisplayColors] = useState(colors);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [shouldScaleIn, setShouldScaleIn] = useState(false);
  const prevColorFormatRef = useRef(colorFormat);
  const prevColorsRef = useRef(colors);
  const isInitialMount = useRef(true);
  const hasCompletedInitialAnimation = useRef(false);

  const handleCopy = async (hex) => {
    try {
      const formattedColor = formatColor(hex, displayFormat);
      await navigator.clipboard.writeText(formattedColor);
      if (onCopy) onCopy(formattedColor);
    } catch {}
  };

  // Trigger animation when colorFormat or colors change
  useEffect(() => {
    const formatChanged = prevColorFormatRef.current !== colorFormat;
    const colorsChanged = JSON.stringify(prevColorsRef.current) !== JSON.stringify(colors);
    
    if (isInitialMount.current) {
      // On initial mount, just set the values
      setDisplayFormat(colorFormat);
      setDisplayColors(colors);
      isInitialMount.current = false;
      // Mark initial animation as complete after scale-up animation finishes
      setTimeout(() => {
        hasCompletedInitialAnimation.current = true;
      }, ANIMATION_TIMINGS.INITIAL_ANIMATION_COMPLETE);
    } else if ((formatChanged || colorsChanged) && !isLoading) {
      // Start fade out animation
      setIsFadingOut(true);
      setShouldScaleIn(false);
      
      // Wait for fade-out to complete, then update content and start scale-in
      setTimeout(() => {
        setDisplayFormat(colorFormat);
        setDisplayColors(colors);
        setIsFadingOut(false);
        
        // Start scale-in animation if we have colors
        if (hasColors) {
          setShouldScaleIn(true);
          // Reset scale-in flag after animation completes (estimate: 6 swatches max)
          const maxSwatches = 6;
          setTimeout(() => {
            setShouldScaleIn(false);
          }, ANIMATION_TIMINGS.SCALE_IN_DURATION + (maxSwatches * ANIMATION_TIMINGS.SCALE_IN_STAGGER));
        }
      }, ANIMATION_TIMINGS.FADE_OUT);
    } else if (!isLoading) {
      // Update immediately if no animation needed
      setDisplayFormat(colorFormat);
      setDisplayColors(colors);
    }
    
    prevColorFormatRef.current = colorFormat;
    prevColorsRef.current = colors;
  }, [colorFormat, colors, isLoading, hasColors]);

  // Calculate scale to fit pills within container
  useEffect(() => {
    const calculateScale = () => {
      if (!swatchListRef.current) return;
      
      const container = swatchListRef.current.parentElement;
      if (!container) return;

      // Cache the current transform to restore it
      const currentTransform = swatchListRef.current.style.transform;
      
      // Temporarily remove scale to measure natural size
      swatchListRef.current.style.transform = 'scale(1)';
      swatchListRef.current.style.visibility = 'hidden'; // Hide during measurement to prevent layout shift
      
      // Force reflow
      void swatchListRef.current.offsetWidth;
      
      const containerRect = container.getBoundingClientRect();
      const listRect = swatchListRef.current.getBoundingClientRect();
      
      // Find panel header to account for its height (use fixed height if available)
      const panelHeader = container.querySelector('.panel-header');
      const headerHeight = panelHeader ? 40 : 0; // Use fixed height instead of measuring
      
      // Calculate available space (accounting for padding and header)
      const availableWidth = containerRect.width - 40; // 20px padding on each side
      const availableHeight = containerRect.height - 40 - headerHeight - 20; // padding + header + margin
      
      // Calculate scale needed
      const widthScale = listRect.width > 0 ? availableWidth / listRect.width : 1;
      const heightScale = listRect.height > 0 ? availableHeight / listRect.height : 1;
      const newScale = Math.min(1, widthScale, heightScale);
      
      const finalScale = Math.max(0.5, newScale); // Don't scale below 50%
      
      // Restore visibility - let React handle the transform via state
      swatchListRef.current.style.visibility = 'visible';
      
      setScale(finalScale);
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      calculateScale();
    }, 0);

    window.addEventListener('resize', calculateScale);
    const interval = setInterval(calculateScale, 200); // Check periodically for zoom changes
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateScale);
      clearInterval(interval);
    };
  }, [colors, isLoading]);

  const renderSkeletons = () => {
    return (
      <div className="swatch-list" ref={swatchListRef}>
        <div className="swatch-row">
          <div className="swatch-item skeleton" />
        </div>
        <div className="swatch-row">
          <div className="swatch-item skeleton" />
          <div className="swatch-item skeleton" />
        </div>
        <div className="swatch-row">
          <div className="swatch-item skeleton" />
          <div className="swatch-item skeleton" />
        </div>
        <div className="swatch-row">
          <div className="swatch-item skeleton" />
        </div>
      </div>
    );
  };

  // Show skeletons only during loading
  if (isLoading) {
    return renderSkeletons();
  }

  // Show empty state if no colors (but keep swatches visible during fade-out animation)
  if (!hasColors && !isFadingOut && displayColors.length === 0) {
    return renderSkeletons();
  }

  // Layout: 1 on top, 2 rows of 2, 1 on bottom
  const layout = [
    [displayColors[0]], // top row: 1 pill
    [displayColors[1], displayColors[2]], // middle row 1: 2 pills
    [displayColors[3], displayColors[4]], // middle row 2: 2 pills
    [displayColors[5]], // bottom row: 1 pill
  ].filter(row => row.some(c => c !== undefined)); // remove rows with no colors

  // Generate a random rotation for each color (between -4 and 4 degrees)
  const getRandomRotation = (index) => {
    // Use index as seed for consistent rotation per color
    const seed = (index * 137.508) % 1; // Golden angle approximation for distribution
    return (seed - 0.5) * 8; // Range from -4 to 4 degrees
  };

  return (
    <div 
      className={`swatch-list ${isPageLoaded && !hasCompletedInitialAnimation.current ? 'scale-up' : ''}`}
      ref={swatchListRef}
      style={{ transform: `scale(${scale})` }}
    >
      {layout.map((row, rowIndex) => (
        <div className="swatch-row" key={rowIndex}>
          {row.map((c, itemIndex) => {
            if (!c) return null;
            const hex = typeof c === 'string' ? c : c.hex;
            const isLight = isLightColor(hex);
            const globalIndex = displayColors.indexOf(c);
            const rotation = getRandomRotation(globalIndex);
            const isTopRow = rowIndex === 0;
            const isBottomRow = rowIndex === layout.length - 1;
            const isMiddleRow = !isTopRow && !isBottomRow;
            const isLeftInMiddleRow = isMiddleRow && itemIndex === 0;
            const isRightInMiddleRow = isMiddleRow && itemIndex === row.length - 1;
            
            // Convert hex to rgba for glow effect (25% opacity)
            const hexToRgba = (hexColor) => {
              const hex = hexColor.replace('#', '');
              const r = parseInt(hex.substr(0, 2), 16);
              const g = parseInt(hex.substr(2, 2), 16);
              const b = parseInt(hex.substr(4, 2), 16);
              return `rgba(${r}, ${g}, ${b}, 0.25)`;
            };
            
            // Determine animation delay for scale-in (staggered from top to bottom)
            const getScaleInDelay = () => {
              if (isInitialMount.current || (isPageLoaded && !hasCompletedInitialAnimation.current)) {
                // Initial page load animation
                return `${(ANIMATION_TIMINGS.INITIAL_SCALE_IN_BASE + (globalIndex * ANIMATION_TIMINGS.SCALE_IN_STAGGER)) / 1000}s`;
              } else if (shouldScaleIn) {
                // Scale-in animation after color change (staggered)
                return `${(globalIndex * ANIMATION_TIMINGS.SCALE_IN_STAGGER) / 1000}s`;
              }
              return '0s';
            };
            
            return (
              <button
                className={`swatch-item ${isFadingOut ? 'swatch-fade-out' : ''} ${shouldScaleIn ? 'swatch-scale-in' : ''} ${isTopRow ? 'swatch-top-row' : ''} ${isBottomRow ? 'swatch-bottom-row' : ''} ${isLeftInMiddleRow ? 'swatch-left-middle' : ''} ${isRightInMiddleRow ? 'swatch-right-middle' : ''}`}
                key={hex}
                style={{ 
                  background: hex,
                  '--base-rotation': `${rotation}deg`,
                  '--swatch-glow': hexToRgba(hex),
                  '--swatch-index': globalIndex,
                  animationDelay: getScaleInDelay()
                }}
                onClick={() => handleCopy(hex)}
                title="Click to copy"
              >
                <span className={`label ${isLight ? 'label-light' : ''}`}>
                  <CopyIcon />
                  <b>{formatColor(hex, displayFormat)}</b>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
